/* eslint-disable camelcase */
/* eslint-disable no-unused-expressions */
/* eslint-disable react/button-has-type */
import React, {
  FC, useState, useEffect,
} from 'react';
import {
  Button, Upload, Form, message, Typography,
} from 'antd';
import {
  UploadOutlined, CloseOutlined,
} from '@ant-design/icons';
import type { UploadRequestOption, UploadProgressEvent } from 'rc-upload/lib/interface';
import request from '@/utils/request';
import styles from './index.module.less';

const { Text } = Typography;

interface UploadProps {
  initialValues?: string,
  reset: boolean,
  tips: string,
  accept: string, // 末尾会自动追加 */*：rc-upload 按 accept 静默过滤文件，*/* 使其放行，实际拦截在 beforeUpload
  required?: boolean, // 是否必填(用于区分网页报告和文件报告)
  uploadMaxSize?: number,
  uploadApi: string;
  name?: string,
  productId?: number,
  openid?: string, // 网页文件的openid
  onFinishUpload: (v: UploadResponse) => void,
}
const PromptText = [
  '请帮我打包成 HTML 报告的 ZIP 压缩包，要求：',
  '1.HTML 文件放在压缩包根目录，作为唯一入口文件',
  '2.所有资源（图片、CSS、JS 等）一并打包，使用相对路径引用',
  '3.不要使用绝对路径或引用外部 CDN',
  '4.页面需做响应式，适配 PC 和移动端',
];

interface UploadResponse {
  tmp_id: number;
  file_name: string;
  openid: string;
}

interface ErrorResponse {
  message?: string;
  errors?: {
    detail: {
      file?: string[];
    }
  };
}

const HtmlZipUpload: FC<UploadProps> = ({
  tips, accept, required = true, initialValues, reset, uploadMaxSize = 1024,
  name = 'media', uploadApi, productId, openid, onFinishUpload,
}: UploadProps) => {
  const [htmlList, setHtmlList] = useState<any[]>([]);
  const [isError, setIsError] = useState<boolean>(false);
  useEffect(() => {
    if (initialValues && !reset) {
      setHtmlList([{
        name: initialValues, uid: '1', size: 0, type: '',
      }]);
    } else {
      setHtmlList([]);
    }
  }, [initialValues, reset]);
  // 文件上传props
  const props: any = {
    maxCount: 1,
    // rc-upload 会按 accept 静默过滤（非 zip 无提示被丢弃），末尾追加 */* 放行所有文件，拦截收敛到 beforeUpload
    accept: `${accept},*/*`,
    name: 'file',
    beforeUpload(file: any) {
      const isLtMaxSize = file.size / 1024 / 1024 < uploadMaxSize;
      if (!isLtMaxSize) {
        message.error('上传文件不能超过1GB!');
        return Upload.LIST_IGNORE;
      }
      // 非 zip 文件统一在此拦截提示（accept 中的 */* 保证选择器和拖拽的文件都能到达此处）
      const isZip = /\.zip$/i.test(file.name);
      if (!isZip) {
        message.error('上传失败，请重新上传');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    action: uploadApi,
    showUploadList: {
      showRemoveIcon: true,
      removeIcon: <CloseOutlined className={styles.tipsIcon} />,
    },
    progress: {
      strokeColor: {
        '0%': '#495ea3',
        '100%': '#495ea3',
      },
      format: (percent: number) => percent && `${parseFloat(percent.toFixed(2))}%`,
    },
    onChange(info: any) {
      const isLtMaxSize = info?.file?.size / 1024 / 1024 < uploadMaxSize;
      if (!isLtMaxSize) {
        return;
      }
      if (info.file.status === 'removed') {
        setHtmlList([]);
      } else if (info.file.status === 'error') {
        setHtmlList([{ ...info.file, error: { message: info.file.error.message } }]);
      } else {
        setHtmlList([info.file]);
      }
    },
    async customRequest(options: UploadRequestOption) {
      const {
        action, file, onProgress, onSuccess, onError,
      } = options;
      const formData = new FormData();
      formData.append('file', file);
      if (openid) {
        formData.append('openid', openid);
      }
      try {
        const res = await request.post<UploadResponse>(action, formData, {
          headers: {
            szid: productId ? String(productId) : undefined,
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress?.({ percent } as unknown as UploadProgressEvent);
          },
          timeout: 0,
          // 上传报错（如 zip 文件校验失败）由本组件自行处理，跳过全局错误提示
          skipErrorHandler: true,
        });
        onSuccess?.(res.data, null as any);
        onFinishUpload(res.data);
      } catch (error) {
        const err = error as ErrorResponse;
        // 断网等网络异常：axios 无 response，错误信息固定为 'Network Error'
        if (err.message === 'Network Error') {
          message.error('网络异常，请重新上传');
          onError?.(new Error('网络异常，请重新上传'));
          return;
        }
        // 文件 error 态展示接口具体报错，接口未返回具体信息时展示默认文案
        const errorMessage = err?.errors?.detail?.file?.[0] || err?.message || '上传失败';
        message.error(errorMessage);
        onError?.(new Error(errorMessage));
      }
    },
  };

  if (productId) {
    props.headers = {
      szid: String(productId),
    };
  }

  const validateFileUpload = () => {
    if (!required) return Promise.resolve();
    const currentFile = htmlList?.[0];
    if (!currentFile) {
      setIsError(true);
      return Promise.reject(new Error('请上传附件!'));
    }
    // 上传失败的文件（如zip校验不通过）不允许提交
    if (currentFile.status === 'error') {
      setIsError(true);
      return Promise.reject(new Error('文件上传失败，请重新上传'));
    }
    setIsError(false);
    return Promise.resolve();
  };

  const handleCopy = async () => {
    const text = PromptText.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      message.success('复制成功');
    } catch (error) {
      console.error('复制失败:', error);
    }
  };

  return (
    <>
      <Form.Item
        className={styles.bigFileUpload}
        label="上传附件"
        name={name}
        rules={[{
          required,
          validator: () => validateFileUpload(),
        }]}
        normalize={(value) => {
          const { response, status } = value.file;
          if (status === 'error') {
            return '';
          }
          if (response) {
            // html上传响应(tmp_id/file_name/openid)没有 Location 字段，此处返回 undefined，
            // 保证提交时 pdf_file 为空，只携带 tmp_id、openid
            return response.Location;
          }
          return value.file;
        }}
      >
        <Upload
          {...props}
          fileList={htmlList}
        >
          <Button className={isError ? styles.uploadError : ''}>
            <UploadOutlined />
            {' '}
            上传文件
          </Button>
          {htmlList && htmlList.length === 0 && (
            <Text className={styles.uploadTipsText}>
              {tips}
            </Text>
          )}
          <div
            className={styles.copy}
            onClick={(e) => e.stopPropagation()}
          >
            <span>发送给AI助手打包</span>
            <ul className={styles.copyText}>
              {PromptText.map((text) => <li key={text}>{text}</li>)}
            </ul>
            <Button onClick={handleCopy}>
              一键复制
            </Button>
          </div>
        </Upload>
      </Form.Item>
      <Form.Item name="tmp_id" hidden />
      <Form.Item name="openid" hidden />
    </>
  );
};

export default HtmlZipUpload;
